import React, { useState, useEffect, useMemo } from 'react';
import { X, Settings, Save, Loader2 } from 'lucide-react';
import { Button } from '../../../ui/Button';
import { Input } from '../../../ui/Input';
import { useWorkflowSchema } from '../../../../hooks/workflows/useWorkflowSchema';
import { useParameterValidation } from '../../../../hooks/workflows/useParameterValidation';
import { ParameterSection } from './ParameterSection';
import { EdgeSection } from './EdgeSection';
import { CanvasNode, CanvasEdge } from '../../../../hooks/workflows/useWorkflowOperations';
import { useToast } from '../../../../hooks/useToast';

interface NodeConfigPanelProps {
  node: {
    id: string;
    type: string;
    position: { x: number; y: number };
    data: any;
  };
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  onUpdate: (node: any) => void;
  onEdgeUpdate: (edges: CanvasEdge[]) => void;
  onClose: () => void;
  onCreateNewNode?: () => void;
}

type ParameterValue = string | number | boolean | object | string[] | any;
type Parameters = Record<string, ParameterValue>;

export const NodeConfigPanel: React.FC<NodeConfigPanelProps> = ({
  node,
  nodes,
  edges,
  onUpdate,
  onEdgeUpdate,
  onClose,
  onCreateNewNode
}) => {
  const [nodeName, setNodeName] = useState(node.data.name || '');
  const [parameters, setParameters] = useState<Parameters>(node.data.config?.parameters || {});
  
  const { parameterTemplates, loading } = useWorkflowSchema();
  const template = useMemo(() => parameterTemplates[node.type] || {}, [parameterTemplates, node.type]);
  const { showToast } = useToast();

  const { errors, validateParameters, clearError, setErrors } = useParameterValidation(template);

  useEffect(() => {
    // Reset local state when node changes
    setNodeName(node.data.name || '');
    setErrors({}); // Clear any validation errors
    
    let initialParams: Parameters = {};
    
    // Use existing parameters or start with empty object
    if (node.data.config?.parameters) {
      initialParams = { ...node.data.config.parameters };
    }
    
    // Set defaults for missing fields
    Object.entries(template).forEach(([key, config]) => {
      if (!(key in initialParams)) {
        initialParams[key] = config.default;
      }
    });
    setParameters(initialParams);
  }, [node.id, node.data.config?.parameters, node.data.name, node.type, template, setErrors]);

  const handleParameterChange = (key: string, value: any) => {
    setParameters(prev => ({ ...prev, [key]: value }));
    clearError(key);
  };

  // Edge management functions
  const handleEdgeAdd = (targetNodeId: string, condition: any) => {
    const newEdge: CanvasEdge = {
      id: `${condition.type}-${node.id}-${targetNodeId}`,
      source: node.id,
      target: targetNodeId,
      condition
    };
    onEdgeUpdate([...edges, newEdge]);
  };

  const handleEdgeDelete = (edgeId: string) => {
    const updatedEdges = edges.filter(edge => edge.id !== edgeId);
    onEdgeUpdate(updatedEdges);
  };

  const handleSave = () => {
    if (!validateParameters(parameters)) {
      showToast('Please fix validation errors before saving', 'error');
      return;
    }

    try {
      const updatedNode = {
        ...node,
        data: {
          ...node.data,
          name: nodeName.trim() || node.type.replace('Activity', '').replace(/([A-Z])/g, ' $1').trim(),
          config: {
            ...node.data.config,
            parameters: parameters
          }
        }
      };
      
      onUpdate(updatedNode);
      onClose();
    } catch (error) {
      console.error('Failed to save node configuration:', error);
      showToast('Failed to save configuration. Please try again.', 'error');
    }
  };


  return (
    <div 
      className="absolute right-0 top-0 h-full w-80 bg-white border-l border-gray-200 shadow-lg z-30 flex flex-col"
      role="dialog"
      aria-labelledby="node-config-title"
      aria-modal="true"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <Settings className="w-5 h-5 text-blue-600" aria-hidden="true" />
          <h3 id="node-config-title" className="font-medium text-gray-900">Configure Node</h3>
        </div>
        <button
          id="close-node-config-btn"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close node configuration panel"
        >
          <X className="w-5 h-5" aria-hidden="true" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Node Name */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Node Name
          </label>
          <Input
            value={nodeName}
            onChange={(e) => setNodeName(e.target.value)}
            placeholder="Enter node name"
          />
        </div>


        {/* Parameters */}
        {loading ? (
          <div className="text-center py-8 text-gray-500">
            <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2">
              <Loader2 className="w-6 h-6" />
            </div>
          </div>
        ) : (
          /* Parameters */
          <ParameterSection
            template={template}
            parameters={parameters}
            errors={errors}
            onParameterChange={handleParameterChange}
          />
        )}

        {/* Edge Management */}
        <EdgeSection
          nodeId={node.id}
          nodes={nodes}
          edges={edges}
          onEdgeAdd={handleEdgeAdd}
          onEdgeDelete={handleEdgeDelete}
          onEdgeUpdate={onEdgeUpdate}
          onCreateNewNode={onCreateNewNode}
        />
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 space-y-2">
        <Button
          id="save-node-config-btn"
          onClick={handleSave}
          className="w-full"
          aria-label="Save node configuration and close panel"
        >
          <Save className="w-4 h-4 mr-2" aria-hidden="true" />
          Save Configuration
        </Button>
        <Button
          variant="ghost"
          onClick={onClose}
          className="w-full"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};